import { ref, computed } from "vue";
import { defineStore, storeToRefs } from "pinia";
import useMyFetch from "@stores/useMyFetch";
import handleError from "@utils/composables/handleError";
import { useMCampaignStore } from "@stores/mCampaign";
import { useMCreativeStore } from "@stores/mCreative";
import { useMAssetLibraryStore } from "@stores/mAssetLibrary";
import { deepCopy } from "@utils/toolBox";
import { captureEvent } from "@/lib/analytics";

export const useMMediumUploadsStore = defineStore("mMediumUploads", () => {
  const mCampaignStore = useMCampaignStore();
  const { currentMCampaign } = storeToRefs(mCampaignStore);

  const mCreativeStore = useMCreativeStore();
  const { mCreative } = storeToRefs(mCreativeStore);

  const assetLibraryStore = useMAssetLibraryStore();
  const { selectedIds } = storeToRefs(assetLibraryStore);

  const mMediumUploads = ref([]);

  const feedVideos = computed(() => {
    return mMediumUploads.value.filter((el) => el.media_type === "video" && el.placement === "feed");
  });

  const activeFeedVideos = computed(() => {
    return feedVideos.value.filter((el) => mCreative.value.m_medium_upload_ids.some((id) => id === el.id));
  });

  const storyVideos = computed(() => {
    return mMediumUploads.value.filter((el) => el.media_type === "video" && el.placement === "story");
  });

  const activeStoryVideos = computed(() => {
    return storyVideos.value.filter((el) => mCreative.value.m_medium_upload_ids.some((id) => id === el.id));
  });

  const images = computed(() => {
    return mMediumUploads.value.filter((el) => el.media_type === "image");
  });

  const activeImages = computed(() => {
    return images.value.filter((el) => mCreative.value.m_medium_upload_ids.some((id) => id === el.id));
  });

  const fetchMMediumUploadsCampaign = async () => {
    try {
      const mCampaignToken = currentMCampaign.value.token;
      const { response, data } = await useMyFetch(
        `/m_medium_uploads?m_campaign_token=${mCampaignToken}&type=image`,
      ).json();

      if (response.value.ok) {
        mMediumUploads.value = deepCopy(data.value);
      }
      return response.value.ok;
    } catch (err) {
      handleError(err);
      return false;
    }
  };

  const toggleMMediumUpload = (mMedium, uniq = false) => {
    const index = mCreative.value.m_medium_upload_ids.findIndex((el) => el === mMedium.id);

    if (index > -1) mCreative.value.m_medium_upload_ids.splice(index, 1);
    else {
      if (uniq === true) {
        clearMMediumUpload(mMedium.placement);
        mCreative.value.m_medium_upload_ids.push(mMedium.id);
      } else {
        mCreative.value.m_medium_upload_ids.push(mMedium.id);
      }
    }
  };

  const clearMMediumUpload = (placement) => {
    const idsToRemove = mMediumUploads.value.filter((el) => el.placement === placement).map((el) => el.id);

    idsToRemove.forEach((id) => {
      const i = mCreative.value.m_medium_upload_ids.findIndex((el) => el === id);
      if (i > -1) mCreative.value.m_medium_upload_ids.splice(i, 1);
    });
  };

  const isMMediumUploadSelected = (id) => {
    return mCreative.value.m_medium_upload_ids?.some((el) => el === id);
  };

  const addMMediumUpload = async (file, type, placement) => {
    const hosted = await assetLibraryStore.uploadFile(file);
    if (!hosted?.url) return {};

    const medium = await assetLibraryStore.createAsset({
      url: hosted.url,
      name: file.name,
      mediaType: type,
      placement,
      campaign: { id: currentMCampaign.value.id, token: currentMCampaign.value.token },
    });
    if (!medium) return {};

    mMediumUploads.value = [deepCopy(medium), ...mMediumUploads.value];
    return medium;
  };

  const updateCampaignStatus = async (m_campaign_token, m_medium_upload_id, method) => {
    try {
      const formData = new FormData();
      formData.append("m_campaign_token", m_campaign_token);
      formData.append("m_medium_upload_id", m_medium_upload_id);
      formData.append("method", method);

      const result = await useMyFetch(`/m_medium_uploads/toggle_mmu_campaign`).post(formData).json();

      if (result.response.value.ok) captureEvent("media_campaign_status_toggled", { m_campaign_token, method });

      return result;
    } catch (err) {
      handleError(err);
      return {};
    }
  };

  // Library edits (rename, retag) also apply to the images already in the campaign pool.
  assetLibraryStore.$onAction(({ name, after }) => {
    if (name !== "updateAsset") return;
    after((updated) => {
      const medium = updated && mMediumUploads.value.find((item) => item.id === updated.id);
      if (!medium) return;
      medium.name = updated.name;
      medium.asset_tags = updated.asset_tags;
    });
  });

  //////////////////////////
  //  Image Library Modal //
  //////////////////////////

  const isModalLibraryVisible = ref(false);

  // The library selection is the draft of the current creative: it is seeded with the
  // creative's images when the modal opens and only flushed to mCreative on Save.
  const initialDraftMMediumUploadIds = ref([]);

  const draftAddedIds = computed(() =>
    selectedIds.value.filter((id) => !initialDraftMMediumUploadIds.value.includes(id)),
  );
  const draftRemovedIds = computed(() =>
    initialDraftMMediumUploadIds.value.filter((id) => !selectedIds.value.includes(id)),
  );
  const hasDraftChanges = computed(() => draftAddedIds.value.length > 0 || draftRemovedIds.value.length > 0);

  const openModalLibrary = () => {
    initialDraftMMediumUploadIds.value = [...(mCreative.value.m_medium_upload_ids || [])];
    isModalLibraryVisible.value = true;
    assetLibraryStore.open("picker", {
      selection: initialDraftMMediumUploadIds.value,
      inUse: initialDraftMMediumUploadIds.value,
      placements: Object.keys(mCreative.value.placements || {}).filter((placement) => mCreative.value.placements[placement]),
      campaign: currentMCampaign.value?.token ?? null,
    });
  };

  const closeModalLibrary = () => {
    isModalLibraryVisible.value = false;
    assetLibraryStore.setEditedAsset(null);
  };

  const applyDraftSelection = async () => {
    const newlyAdded = draftAddedIds.value;
    if (newlyAdded.length > 0) {
      const campaignIds = new Set(mMediumUploads.value.map((m) => m.id));
      for (const id of newlyAdded) {
        if (!campaignIds.has(id)) {
          await updateCampaignStatus(currentMCampaign.value.token, id, "add");
        }
      }
      await fetchMMediumUploadsCampaign();
    }
    mCreative.value.m_medium_upload_ids = [...selectedIds.value];
    closeModalLibrary();
  };

  const toggleModalLibrary = () => {
    if (isModalLibraryVisible.value) closeModalLibrary();
    else openModalLibrary();
  };

  return {
    feedVideos,
    storyVideos,
    activeFeedVideos,
    activeStoryVideos,
    images,
    activeImages,
    mMediumUploads,
    fetchMMediumUploadsCampaign,
    toggleMMediumUpload,
    isMMediumUploadSelected,
    addMMediumUpload,
    currentMCampaign,

    //// Library modal

    isModalLibraryVisible,
    openModalLibrary,
    closeModalLibrary,
    toggleModalLibrary,
    draftAddedIds,
    draftRemovedIds,
    hasDraftChanges,
    applyDraftSelection,
  };
});
